import {Injectable} from '@angular/core';
import {Headers, Http, Response} from '@angular/http';
import {Platform} from 'ionic-angular';
import {Observable} from 'rxjs/Observable';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import 'rxjs/add/operator/map';

import {AuthProvider} from '../auth/auth';
import * as Config from '../../app/app.config';

export interface ActivityEvent {
  type: 'camera' | 'microphone';
  event: 'on' | 'off' | 'blocked' | 'noise' | 'silence';
  timestamp: string;
  data?: any;
}

export interface ActivitySummary {
  camera: {
    enabled: boolean;
    activeDuration: number;
    inactiveCount: number;
    blockedCount: number;
  };
  microphone: {
    enabled: boolean;
    activeDuration: number;
    silenceDuration: number;
    noiseDetectedCount: number;
  };
  events: ActivityEvent[];
}

@Injectable()
export class ActivityMonitorProvider {

  private userProofId: number = null;
  private activityId: number = null;
  private isMonitoring: boolean = false;

  // Media streams
  private cameraStream: MediaStream = null;
  private microphoneStream: MediaStream = null;

  // Audio analysis
  private audioContext: AudioContext = null;
  private analyser: AnalyserNode = null;
  private audioCheckInterval: any = null;

  // Tracking state
  private cameraEnabled: boolean = false;
  private microphoneEnabled: boolean = false;
  private cameraStartTime: number = null;
  private microphoneStartTime: number = null;
  private silenceStartTime: number = null;

  // Counters and durations
  private cameraActiveDuration: number = 0;
  private cameraInactiveCount: number = 0;
  private cameraBlockedCount: number = 0;
  private microphoneActiveDuration: number = 0;
  private microphoneSilenceDuration: number = 0;
  private microphoneNoiseDetectedCount: number = 0;

  // Event log
  private eventLog: ActivityEvent[] = [];

  // BehaviorSubjects for reactive updates (keeps last value for new subscribers)
  private _cameraStatus = new BehaviorSubject<boolean>(false);
  private _microphoneStatus = new BehaviorSubject<boolean>(false);
  private _audioLevel = new BehaviorSubject<number>(0);

  // Audio level threshold for noise detection (0-255)
  private noiseThreshold: number = 30;
  private silenceThreshold: number = 10;
  private isInSilence: boolean = true;

  constructor(
    private platform: Platform,
    private http: Http,
    private authProvider: AuthProvider
  ) {
    console.log('ActivityMonitorProvider initialized');
  }

  /**
   * Start activity monitoring for a user proof
   * @param userProofId The user proof ID
   * @param requestMedia Whether to request media permissions (default false, as external components handle media)
   */
  startMonitoring(userProofId: number, requestMedia: boolean = false): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.isMonitoring) {
        console.log('ActivityMonitorProvider - Already monitoring');
        resolve(true);
        return;
      }

      this.userProofId = userProofId;
      this.resetCounters();

      this.platform.ready().then(() => {
        // Start monitoring on the backend
        this.startMonitoringOnServer().subscribe(
          (response) => {
            if (response.success) {
              this.activityId = response.activityId;
              console.log('ActivityMonitorProvider - Monitoring started, activityId:', this.activityId);
            } else {
              console.log('ActivityMonitorProvider - Server response not success, continuing anyway');
            }

            this.isMonitoring = true;

            if (requestMedia) {
              this.requestMediaPermissions().then(() => {
                resolve(true);
              }).catch((err) => {
                console.log('ActivityMonitorProvider - Media permission error:', err);
                resolve(true);
              });
            } else {
              console.log('ActivityMonitorProvider - Skipping media request, waiting for external setCameraActive/setMicrophoneActive calls');
              resolve(true);
            }
          },
          (error) => {
            console.log('ActivityMonitorProvider - Server error, continuing with local monitoring:', error);
            this.isMonitoring = true;

            if (requestMedia) {
              this.requestMediaPermissions().then(() => {
                resolve(true);
              }).catch((err) => {
                console.log('ActivityMonitorProvider - Media permission error:', err);
                resolve(true);
              });
            } else {
              resolve(true);
            }
          }
        );
      });
    });
  }

  /**
   * Request camera and microphone permissions
   */
  private requestMediaPermissions(): Promise<void> {
    return new Promise((resolve) => {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.log('ActivityMonitorProvider - MediaDevices API not available');
        this.logEvent('camera', 'blocked', {reason: 'api_not_available'});
        this.logEvent('microphone', 'blocked', {reason: 'api_not_available'});
        this.cameraBlockedCount++;
        // Resolve instead of reject to continue monitoring without media
        resolve();
        return;
      }

      // First check what devices are available
      this.enumerateDevices().then((devices) => {
        const hasCamera = devices.some(d => d.kind === 'videoinput');
        const hasMicrophone = devices.some(d => d.kind === 'audioinput');

        console.log('ActivityMonitorProvider - Available devices: camera=' + hasCamera + ', microphone=' + hasMicrophone);

        if (!hasCamera && !hasMicrophone) {
          // No devices available at all
          console.log('ActivityMonitorProvider - No media devices available');
          this.logEvent('camera', 'blocked', {reason: 'no_device'});
          this.logEvent('microphone', 'blocked', {reason: 'no_device'});
          this.cameraBlockedCount++;
          resolve();
          return;
        }

        // Build constraints based on available devices
        const constraints: any = {};
        if (hasCamera) constraints.video = true;
        if (hasMicrophone) constraints.audio = true;

        navigator.mediaDevices.getUserMedia(constraints)
          .then((stream) => {
            console.log('ActivityMonitorProvider - Media permissions granted');
            this.handleMediaStreamSuccess(stream, hasCamera, hasMicrophone);

            // Log blocked for missing devices
            if (!hasCamera) {
              this.logEvent('camera', 'blocked', {reason: 'no_device'});
              this.cameraBlockedCount++;
            }
            if (!hasMicrophone) {
              this.logEvent('microphone', 'blocked', {reason: 'no_device'});
            }

            resolve();
          })
          .catch((err) => {
            console.log('ActivityMonitorProvider - getUserMedia failed:', err.name, err.message);
            this.handleMediaError(err, hasCamera, hasMicrophone, resolve);
          });
      }).catch((err) => {
        console.log('ActivityMonitorProvider - enumerateDevices failed:', err);
        // Fallback to trying without checking devices first
        this.tryMediaWithFallback(resolve);
      });
    });
  }

  /**
   * Enumerate available media devices
   */
  private enumerateDevices(): Promise<MediaDeviceInfo[]> {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      return navigator.mediaDevices.enumerateDevices();
    }
    return Promise.resolve([]);
  }

  /**
   * Handle media access error with fallbacks
   */
  private handleMediaError(err: any, hasCamera: boolean, hasMicrophone: boolean, resolve: () => void): void {
    // Try individual devices if combined request failed
    if (hasCamera && hasMicrophone) {
      // Try audio only first
      this.tryAudioOnly()
        .then(() => {
          this.logEvent('camera', 'blocked', {reason: err.name});
          this.cameraBlockedCount++;
          resolve();
        })
        .catch(() => {
          // Try video only
          this.tryVideoOnly()
            .then(() => {
              this.logEvent('microphone', 'blocked', {reason: err.name});
              resolve();
            })
            .catch(() => {
              this.logAllBlocked(err.name);
              resolve();
            });
        });
    } else if (hasMicrophone) {
      this.logEvent('camera', 'blocked', {reason: 'no_device'});
      this.logEvent('microphone', 'blocked', {reason: err.name});
      this.cameraBlockedCount++;
      resolve();
    } else if (hasCamera) {
      this.logEvent('camera', 'blocked', {reason: err.name});
      this.logEvent('microphone', 'blocked', {reason: 'no_device'});
      this.cameraBlockedCount++;
      resolve();
    } else {
      this.logAllBlocked(err.name);
      resolve();
    }
  }

  /**
   * Log all devices as blocked
   */
  private logAllBlocked(reason: string): void {
    console.log('ActivityMonitorProvider - All media blocked:', reason);
    this.logEvent('camera', 'blocked', {reason: reason});
    this.logEvent('microphone', 'blocked', {reason: reason});
    this.cameraBlockedCount++;
    this.sendEventToServer('camera', 'blocked', {reason: reason});
    this.sendEventToServer('microphone', 'blocked', {reason: reason});
  }

  /**
   * Try media with fallback (used when enumerateDevices fails)
   */
  private tryMediaWithFallback(resolve: () => void): void {
    navigator.mediaDevices.getUserMedia({video: true, audio: true})
      .then((stream) => {
        console.log('ActivityMonitorProvider - Media permissions granted (video + audio)');
        this.handleMediaStreamSuccess(stream, true, true);
        resolve();
      })
      .catch(() => {
        this.tryAudioOnly()
          .then(() => {
            this.logEvent('camera', 'blocked', {reason: 'not_available'});
            this.cameraBlockedCount++;
            resolve();
          })
          .catch(() => {
            this.tryVideoOnly()
              .then(() => {
                this.logEvent('microphone', 'blocked', {reason: 'not_available'});
                resolve();
              })
              .catch((err) => {
                this.logAllBlocked(err.name || 'unknown');
                resolve();
              });
          });
      });
  }

  /**
   * Try to get audio only stream
   */
  private tryAudioOnly(): Promise<void> {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices.getUserMedia({audio: true, video: false})
        .then((stream) => {
          console.log('ActivityMonitorProvider - Audio only permissions granted');
          this.handleMediaStreamSuccess(stream, false, true);
          // Log camera as blocked since we couldn't get it
          this.logEvent('camera', 'blocked', {reason: 'device_not_found'});
          this.cameraBlockedCount++;
          this.sendEventToServer('camera', 'blocked', {reason: 'device_not_found'});
          resolve();
        })
        .catch((err) => {
          console.log('ActivityMonitorProvider - Audio only also failed:', err.name);
          reject(err);
        });
    });
  }

  /**
   * Try to get video only stream
   */
  private tryVideoOnly(): Promise<void> {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices.getUserMedia({video: true, audio: false})
        .then((stream) => {
          console.log('ActivityMonitorProvider - Video only permissions granted');
          this.handleMediaStreamSuccess(stream, true, false);
          // Log microphone as blocked since we couldn't get it
          this.logEvent('microphone', 'blocked', {reason: 'device_not_found'});
          this.sendEventToServer('microphone', 'blocked', {reason: 'device_not_found'});
          resolve();
        })
        .catch((err) => {
          console.log('ActivityMonitorProvider - Video only also failed:', err.name);
          reject(err);
        });
    });
  }

  /**
   * Handle successful media stream acquisition
   */
  private handleMediaStreamSuccess(stream: MediaStream, hasVideo: boolean, hasAudio: boolean): void {
    if (hasVideo) {
      this.logEvent('camera', 'on');
      this.cameraStream = stream;
      this.startCameraTracking();
    }

    if (hasAudio) {
      this.logEvent('microphone', 'on');
      this.microphoneStream = stream;
      this.startMicrophoneTracking(stream);
    }
  }

  /**
   * Start camera tracking
   */
  private startCameraTracking(): void {
    // Check if stream has video tracks
    if (!this.cameraStream) {
      console.log('ActivityMonitorProvider - No camera stream');
      return;
    }

    const videoTracks = this.cameraStream.getVideoTracks();
    if (!videoTracks || videoTracks.length === 0) {
      console.log('ActivityMonitorProvider - No video tracks in stream');
      return;
    }

    this.cameraEnabled = true;
    this.cameraStartTime = Date.now();
    this._cameraStatus.next(true);

    // Monitor camera track state
    const videoTrack = videoTracks[0];
    if (videoTrack) {
      videoTrack.onended = () => {
        this.onCameraDisabled();
      };
      videoTrack.onmute = () => {
        this.onCameraDisabled();
      };
      videoTrack.onunmute = () => {
        this.onCameraEnabled();
      };
    }
  }

  /**
   * Start microphone tracking with audio level analysis
   */
  private startMicrophoneTracking(stream: MediaStream): void {
    // Check if stream has audio tracks
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks || audioTracks.length === 0) {
      console.log('ActivityMonitorProvider - No audio tracks in stream');
      return;
    }

    this.microphoneEnabled = true;
    this.microphoneStartTime = Date.now();
    this._microphoneStatus.next(true);

    // Set up audio analysis
    try {
      const AudioContextClass = window['AudioContext'] || window['webkitAudioContext'];
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 256;

        const source = this.audioContext.createMediaStreamSource(stream);
        source.connect(this.analyser);

        // Start audio level monitoring
        this.startAudioLevelMonitoring();
      }
    } catch (err) {
      console.log('ActivityMonitorProvider - Audio context error:', err);
    }

    // Monitor audio track state
    const audioTrack = audioTracks[0];
    if (audioTrack) {
      audioTrack.onended = () => {
        this.onMicrophoneDisabled();
      };
      audioTrack.onmute = () => {
        this.onMicrophoneDisabled();
      };
      audioTrack.onunmute = () => {
        this.onMicrophoneEnabled();
      };
    }
  }

  /**
   * Monitor audio levels for noise/silence detection
   */
  private startAudioLevelMonitoring(): void {
    if (!this.analyser) return;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    this.silenceStartTime = Date.now();
    this.isInSilence = true;

    this.audioCheckInterval = setInterval(() => {
      if (!this.analyser) return;

      this.analyser.getByteFrequencyData(dataArray);

      // Calculate average audio level
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const averageLevel = sum / bufferLength;

      this._audioLevel.next(averageLevel);

      // Check for noise/silence transitions
      if (averageLevel > this.noiseThreshold && this.isInSilence) {
        // Transition from silence to noise
        this.isInSilence = false;
        this.microphoneNoiseDetectedCount++;
        this.logEvent('microphone', 'noise', {level: averageLevel});

        // Calculate silence duration
        if (this.silenceStartTime) {
          this.microphoneSilenceDuration += Math.floor((Date.now() - this.silenceStartTime) / 1000);
        }
      } else if (averageLevel < this.silenceThreshold && !this.isInSilence) {
        // Transition from noise to silence
        this.isInSilence = true;
        this.silenceStartTime = Date.now();
        this.logEvent('microphone', 'silence', {level: averageLevel});
      }
    }, 500); // Check every 500ms
  }

  /**
   * Handle camera disabled event
   */
  private onCameraDisabled(): void {
    if (this.cameraEnabled && this.cameraStartTime) {
      this.cameraActiveDuration += Math.floor((Date.now() - this.cameraStartTime) / 1000);
    }
    this.cameraEnabled = false;
    this.cameraStartTime = null;
    this.cameraInactiveCount++;
    this._cameraStatus.next(false);
    this.logEvent('camera', 'off');
    this.sendEventToServer('camera', 'off');
  }

  /**
   * Handle camera enabled event
   */
  private onCameraEnabled(): void {
    this.cameraEnabled = true;
    this.cameraStartTime = Date.now();
    this._cameraStatus.next(true);
    this.logEvent('camera', 'on');
    this.sendEventToServer('camera', 'on');
  }

  /**
   * Handle microphone disabled event
   */
  private onMicrophoneDisabled(): void {
    if (this.microphoneEnabled && this.microphoneStartTime) {
      this.microphoneActiveDuration += Math.floor((Date.now() - this.microphoneStartTime) / 1000);
    }
    this.microphoneEnabled = false;
    this.microphoneStartTime = null;
    this._microphoneStatus.next(false);
    this.logEvent('microphone', 'off');
    this.sendEventToServer('microphone', 'off');
  }

  /**
   * Handle microphone enabled event
   */
  private onMicrophoneEnabled(): void {
    this.microphoneEnabled = true;
    this.microphoneStartTime = Date.now();
    this._microphoneStatus.next(true);
    this.logEvent('microphone', 'on');
    this.sendEventToServer('microphone', 'on');
  }

  /**
   * Log an event locally
   */
  private logEvent(type: 'camera' | 'microphone', event: string, data?: any): void {
    const activityEvent: ActivityEvent = {
      type: type,
      event: event as any,
      timestamp: new Date().toISOString(),
      data: data
    };
    this.eventLog.push(activityEvent);
    console.log('ActivityMonitorProvider - Event logged:', activityEvent);
  }

  /**
   * Send event to server
   */
  private sendEventToServer(type: string, event: string, data?: any): void {
    if (!this.userProofId || !this.isMonitoring) return;

    const headers = this.authProvider.createApplicationHeadersWithBearer();
    const body = {
      type: type,
      event: event,
      data: data,
      timestamp: new Date().toISOString()
    };

    this.http.post(
      Config.baseUrl + '/activity-monitor/' + this.userProofId + '/event',
      body,
      {headers: headers}
    ).subscribe(
      (response) => {
        console.log('ActivityMonitorProvider - Event sent to server');
      },
      (error) => {
        console.log('ActivityMonitorProvider - Failed to send event:', error);
      }
    );
  }

  /**
   * Stop monitoring and save summary
   */
  stopMonitoring(): Promise<ActivitySummary> {
    return new Promise((resolve, reject) => {
      if (!this.isMonitoring) {
        console.log('ActivityMonitorProvider - Not monitoring');
        resolve(null);
        return;
      }

      // Calculate final durations
      if (this.cameraEnabled && this.cameraStartTime) {
        this.cameraActiveDuration += Math.floor((Date.now() - this.cameraStartTime) / 1000);
      }
      if (this.microphoneEnabled && this.microphoneStartTime) {
        this.microphoneActiveDuration += Math.floor((Date.now() - this.microphoneStartTime) / 1000);
      }
      if (this.isInSilence && this.silenceStartTime) {
        this.microphoneSilenceDuration += Math.floor((Date.now() - this.silenceStartTime) / 1000);
      }

      // Stop audio monitoring
      if (this.audioCheckInterval) {
        clearInterval(this.audioCheckInterval);
        this.audioCheckInterval = null;
      }

      // Close audio context
      if (this.audioContext) {
        this.audioContext.close().catch(() => {});
        this.audioContext = null;
        this.analyser = null;
      }

      // Stop media streams
      this.stopMediaStreams();

      // Build summary
      const summary: ActivitySummary = {
        camera: {
          enabled: this.cameraEnabled,
          activeDuration: this.cameraActiveDuration,
          inactiveCount: this.cameraInactiveCount,
          blockedCount: this.cameraBlockedCount
        },
        microphone: {
          enabled: this.microphoneEnabled,
          activeDuration: this.microphoneActiveDuration,
          silenceDuration: this.microphoneSilenceDuration,
          noiseDetectedCount: this.microphoneNoiseDetectedCount
        },
        events: this.eventLog
      };

      // Send summary to server
      this.saveSummaryToServer(summary).subscribe(
        (response) => {
          console.log('ActivityMonitorProvider - Summary saved to server');
          this.isMonitoring = false;
          resolve(summary);
        },
        (error) => {
          console.log('ActivityMonitorProvider - Failed to save summary:', error);
          this.isMonitoring = false;
          // Still return the summary even if server save failed
          resolve(summary);
        }
      );
    });
  }

  /**
   * Stop all media streams
   */
  private stopMediaStreams(): void {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => track.stop());
      this.cameraStream = null;
    }
    if (this.microphoneStream && this.microphoneStream !== this.cameraStream) {
      this.microphoneStream.getTracks().forEach(track => track.stop());
      this.microphoneStream = null;
    }
  }

  /**
   * Reset all counters
   */
  private resetCounters(): void {
    this.cameraEnabled = false;
    this.microphoneEnabled = false;
    this.cameraStartTime = null;
    this.microphoneStartTime = null;
    this.silenceStartTime = null;
    this.cameraActiveDuration = 0;
    this.cameraInactiveCount = 0;
    this.cameraBlockedCount = 0;
    this.microphoneActiveDuration = 0;
    this.microphoneSilenceDuration = 0;
    this.microphoneNoiseDetectedCount = 0;
    this.eventLog = [];
    this.isInSilence = true;
  }

  /**
   * Start monitoring on the server
   */
  private startMonitoringOnServer(): Observable<any> {
    const headers = this.authProvider.createApplicationHeadersWithBearer();
    const body = {
      browserInfo: navigator.userAgent,
      startedAt: new Date().toISOString(),
      cameraEnabled: false,
      microphoneEnabled: false
    };

    return this.http.post(
      Config.baseUrl + '/activity-monitor/' + this.userProofId + '/start',
      body,
      {headers: headers}
    ).map((res: Response) => res.json());
  }

  /**
   * Save summary to the server
   */
  private saveSummaryToServer(summary: ActivitySummary): Observable<any> {
    const headers = this.authProvider.createApplicationHeadersWithBearer();

    return this.http.post(
      Config.baseUrl + '/activity-monitor/' + this.userProofId + '/summary',
      summary,
      {headers: headers}
    ).map((res: Response) => res.json());
  }

  /**
   * Get activity data from server
   */
  getActivityData(userProofId: number): Observable<any> {
    const headers = this.authProvider.createApplicationHeadersWithBearer();

    return this.http.get(
      Config.baseUrl + '/activity-monitor/' + userProofId,
      {headers: headers}
    ).map((res: Response) => res.json());
  }

  /**
   * Observable for camera status changes
   */
  getCameraStatus(): Observable<boolean> {
    return this._cameraStatus.asObservable();
  }

  /**
   * Observable for microphone status changes
   */
  getMicrophoneStatus(): Observable<boolean> {
    return this._microphoneStatus.asObservable();
  }

  /**
   * Observable for audio level changes
   */
  getAudioLevel(): Observable<number> {
    return this._audioLevel.asObservable();
  }

  /**
   * Check if currently monitoring
   */
  isCurrentlyMonitoring(): boolean {
    return this.isMonitoring;
  }

  /**
   * Manually set camera as active (for external webcam components like ack-webcam)
   */
  setCameraActive(active: boolean): void {
    console.log('ActivityMonitorProvider - setCameraActive:', active);
    if (active && !this.cameraEnabled) {
      this.cameraEnabled = true;
      this.cameraStartTime = Date.now();
      this._cameraStatus.next(true);
      this.logEvent('camera', 'on', {source: 'external'});
    } else if (!active && this.cameraEnabled) {
      this.onCameraDisabled();
    }
  }

  /**
   * Manually set microphone as active (for external audio components)
   */
  setMicrophoneActive(active: boolean): void {
    console.log('ActivityMonitorProvider - setMicrophoneActive:', active);
    if (active && !this.microphoneEnabled) {
      this.microphoneEnabled = true;
      this.microphoneStartTime = Date.now();
      this._microphoneStatus.next(true);
      this.logEvent('microphone', 'on', {source: 'external'});
    } else if (!active && this.microphoneEnabled) {
      this.onMicrophoneDisabled();
    }
  }

  /**
   * Get current summary without stopping
   */
  getCurrentSummary(): ActivitySummary {
    let currentCameraDuration = this.cameraActiveDuration;
    let currentMicDuration = this.microphoneActiveDuration;
    let currentSilenceDuration = this.microphoneSilenceDuration;

    if (this.cameraEnabled && this.cameraStartTime) {
      currentCameraDuration += Math.floor((Date.now() - this.cameraStartTime) / 1000);
    }
    if (this.microphoneEnabled && this.microphoneStartTime) {
      currentMicDuration += Math.floor((Date.now() - this.microphoneStartTime) / 1000);
    }
    if (this.isInSilence && this.silenceStartTime) {
      currentSilenceDuration += Math.floor((Date.now() - this.silenceStartTime) / 1000);
    }

    return {
      camera: {
        enabled: this.cameraEnabled,
        activeDuration: currentCameraDuration,
        inactiveCount: this.cameraInactiveCount,
        blockedCount: this.cameraBlockedCount
      },
      microphone: {
        enabled: this.microphoneEnabled,
        activeDuration: currentMicDuration,
        silenceDuration: currentSilenceDuration,
        noiseDetectedCount: this.microphoneNoiseDetectedCount
      },
      events: [...this.eventLog]
    };
  }

  /**
   * Destroy the provider
   */
  destroy(): void {
    console.log('ActivityMonitorProvider - Destroyed');
    if (this.isMonitoring) {
      this.stopMonitoring().catch(() => {});
    }
    this.userProofId = null;
    this.activityId = null;
  }
}



// WEBPACK FOOTER //
// ./src/providers/activity-monitor/activity-monitor.ts